import React, { useState } from 'react';
import {
  Settings,
  Sun,
  Moon,
  Globe,
  Image as ImageIcon,
  KeyRound,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  LockKeyhole,
  Home,
  CalendarDays,
  Plus,
} from 'lucide-react';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { TutorialEngine } from '@/components/tutorial/TutorialEngine';
import { cn } from '@/lib/utils';

const PROFILE_NAME = 'SARA';
const PROFILE_PHONE = '600123123';

const STEPS = [
  { id: 'settings-action', target: 'settings-gear', message: 'Pulsa la rueda para configurar este perfil.' },
  { id: 'profile-info', target: 'profile-info', message: 'El nombre identifica el perfil y el teléfono permite disponer de acceso a WhatsApp cuando trabajas con otros perfiles.' },
  { id: 'web-info', target: 'web-info', message: 'Aquí puedes añadir la URL de una web que quieras tener siempre como acceso rápido desde Rutapp.' },
  { id: 'image-info', target: 'image-info', message: 'Aquí puedes subir desde tu móvil una imagen que quieras tener como acceso rápido, por ejemplo planos, plazas o normas.' },
  { id: 'confirm-actions', target: 'confirm-actions', message: 'Activa esta opción si quieres confirmar las acciones importantes antes de aplicarlas.' },
  { id: 'confirm-preview', target: 'confirm-preview', message: 'Con esta opción activa, Rutapp te pedirá confirmación antes de ejecutar estas acciones.' },
  { id: 'pin-info', target: 'pin-info', message: 'Puedes proteger este perfil con un PIN opcional de 4 a 6 dígitos, especialmente útil si varias personas tienen acceso a distintos perfiles.' },
  { id: 'save-settings', target: 'save-settings', message: 'Pulsa Guardar Cambios para aplicar la configuración del perfil.' },
  { id: 'quick-access-saved', target: 'quick-access-saved', message: 'Perfecto. Aquí aparecerán los accesos rápidos guardados de web e imagen.' },
  { id: 'web-action', target: 'web-action', message: 'Pulsa aquí para abrir la web de acceso rápido configurada para este perfil.' },
  { id: 'web-result', target: 'web-result', message: 'Este acceso abre directamente la web que hayas configurado en el perfil.' },
  { id: 'image-action', target: 'image-action', message: 'Pulsa aquí para consultar la imagen de acceso rápido.' },
  { id: 'image-result', target: 'image-result', message: 'Este acceso muestra rápidamente la imagen que hayas guardado en el perfil.' },
  { id: 'theme-action', target: 'theme-action', message: 'Pulsa aquí para cambiar al modo oscuro.' },
  { id: 'theme-result', target: 'theme-result', message: 'Puedes adaptar la apariencia de Rutapp al modo que prefieras.' },
  { id: 'theme-back', target: 'theme-back', message: 'Pulsa de nuevo para volver al modo claro.' },
  { id: 'tutorial-complete', target: 'tutorial-complete', message: '' },
];

function CoachMark({ text, dark = false, themeAware = false }) {
  return (
    <div className="flex justify-center">
      <div
        className={cn(
          'max-w-[300px] rounded-md border px-3 py-2 text-xs font-medium shadow-sm',
          dark
            ? 'border-sky-500/60 bg-slate-800 text-slate-100'
            : themeAware
              ? 'border-primary/40 bg-primary/10 text-slate-900 dark:border-sky-500/60 dark:bg-slate-800 dark:text-slate-100'
              : 'border-primary/40 bg-primary/10 text-slate-900'
        )}
      >
        {text}
      </div>
    </div>
  );
}

function ContinueButton({ onClick, disabled }) {
  return (
    <div className="flex justify-center">
      <Button type="button" size="sm" onClick={onClick} disabled={disabled}>
        Continuar
      </Button>
    </div>
  );
}


const WEEKDAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DAY_LETTERS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const FICT_COUNTS = [2, 3, 1, 4, 2, 0, 0];

function getLocalDateLabel() {
  const now = new Date();
  const weekday = WEEKDAY_NAMES[now.getDay()];
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  return `${weekday} · ${dd}/${mm}/${yy}`;
}

function getWeekDays() {
  const now = new Date();
  const monday = new Date(now);
  const diff = now.getDay() === 0 ? -6 : 1 - now.getDay();
  monday.setDate(now.getDate() + diff);

  return DAY_LETTERS.map((letter, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    const isToday =
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear();
    return { letter, dayNumber: d.getDate(), isToday, count: FICT_COUNTS[i] };
  });
}

function CircleButton({ Icon, highlighted = false, actionHighlight = false, dark = false, onClick, interactive = false }) {
  const cls = cn(
    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border sm:h-10 sm:w-10',
    actionHighlight
      ? cn('border-amber-400 text-primary ring-4 ring-amber-400 ring-offset-2', dark ? 'bg-amber-500/10' : 'bg-amber-50')
      : highlighted
        ? 'border-primary bg-primary/15 text-primary ring-2 ring-primary shadow-sm'
        : dark
          ? 'border-slate-700 bg-slate-800 text-slate-400 opacity-60'
          : 'border-slate-200 bg-slate-100 text-slate-500 opacity-60'
  );
  const icon = <Icon className="h-4 w-4 sm:h-5 sm:w-5" />;

  if (interactive) {
    return (
      <button type="button" onClick={onClick} className={cls}>
        {icon}
      </button>
    );
  }
  return <div className={cls}>{icon}</div>;
}

function ConfirmationExample({ label }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-amber-500/60 bg-amber-500/10 px-3 py-2">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-green-500">
          <Check className="h-4 w-4 text-white" />
        </div>
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-red-500">
          <X className="h-4 w-4 text-white" />
        </div>
      </div>
    </div>
  );
}

function SimulatedMainScreen({
  activeTarget,
  message,
  darkMode,
  onOpenSettings,
  onOpenWeb,
  onOpenImage,
  onToggleTheme,
  onContinue,
}) {
  const isGearTarget = activeTarget === 'settings-gear';
  const isQuickAccessSaved = activeTarget === 'quick-access-saved';
  const isWebAction = activeTarget === 'web-action';
  const isImageAction = activeTarget === 'image-action';
  const isThemeAction = activeTarget === 'theme-action';
  const isThemeResult = activeTarget === 'theme-result';
  const isThemeBack = activeTarget === 'theme-back';

  const isThemeTarget = isThemeAction || isThemeBack;
  const isThemeStep = isThemeAction || isThemeResult || isThemeBack;
  const isRichMain =
    isQuickAccessSaved || isWebAction || isImageAction || isThemeAction || isThemeResult || isThemeBack;

  const t = simTheme(darkMode);
  const ThemeIcon = darkMode ? Sun : Moon;

  return (
    <div className={cn('rounded-lg border p-4', t.screen)}>
      {/* Cabecera simulada */}
      <div className={cn('relative flex items-center justify-between border-b pb-3', darkMode ? 'border-slate-700' : 'border-slate-200')}>
        {isThemeTarget ? (
          <button
            type="button"
            onClick={onToggleTheme}
            aria-label={darkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            title={darkMode ? 'Modo claro' : 'Modo oscuro'}
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full border border-amber-400 ring-4 ring-amber-400 ring-offset-2',
              darkMode ? 'bg-amber-500/10 text-yellow-500' : 'bg-amber-50 text-primary'
            )}
          >
            <ThemeIcon className="h-5 w-5" />
          </button>
        ) : (
          <div className={cn('flex h-10 w-10 items-center justify-center rounded-full opacity-70', darkMode ? 'text-yellow-500' : 'text-primary')}>
            <ThemeIcon className="h-5 w-5" />
          </div>
        )}

        <div className={cn('text-xl font-bold', t.text)}>RUTAPP</div>

        <div className="h-10 w-10" />
      </div>

      {isThemeStep && (
        <div className="mt-3">
          <CoachMark text={message} dark={darkMode} />
          {isThemeResult && (
            <div className="mt-3">
              <ContinueButton onClick={onContinue} />
            </div>
          )}
        </div>
      )}

      {/* Perfil SARA con rueda dentada superpuesta */}
      <div className="mt-5 flex flex-col items-center">
        <div className="relative">
          <div className={cn('flex h-20 w-20 items-center justify-center rounded-full border-2 border-primary', t.mutedBg)}>
            <span className="text-3xl font-bold text-primary">{PROFILE_NAME.charAt(0)}</span>
          </div>

          {/* Rueda Settings: solo este botón abre la configuración */}
          <button
            type="button"
            onClick={onOpenSettings}
            disabled={!isGearTarget}
            aria-label={`Configuración de ${PROFILE_NAME}`}
            title={`Configuración de ${PROFILE_NAME}`}
            className={cn(
              'absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full border border-primary bg-white hover:bg-white/90',
              isGearTarget ? 'ring-2 ring-primary ring-offset-2' : ''
            )}
          >
            <Settings className="h-3.5 w-3.5 text-primary" />
          </button>
        </div>

        <span className={cn('mt-2 text-sm font-semibold', t.text)}>{PROFILE_NAME}</span>

        {isGearTarget && (
          <div className="mt-3">
            <CoachMark text={message} />
          </div>
        )}
      </div>

      {isRichMain ? (
        <div className="mt-4 space-y-3">
          {/* Fecha (presentación local) */}
          <div className="flex items-center justify-center gap-3">
            <div className={cn('flex h-8 w-8 items-center justify-center', t.mutedText)}>
              <ChevronLeft className="h-5 w-5" />
            </div>
            <span className={cn('text-sm font-semibold', t.text)}>{getLocalDateLabel()}</span>
            <div className={cn('flex h-8 w-8 items-center justify-center', t.mutedText)}>
              <ChevronRight className="h-5 w-5" />
            </div>
          </div>

          {/* Fila de días */}
          <div className="flex justify-center gap-0.5 sm:gap-1">
            {getWeekDays().map((d, i) => (
              <div
                key={i}
                className={cn(
                  'flex w-8 shrink-0 flex-col items-center rounded-md border px-0.5 py-1 sm:w-9',
                  d.isToday ? 'border-primary bg-primary/40' : t.card
                )}
              >
                <span className={cn('text-xs uppercase', t.text)}>{d.letter}</span>
                <span className={cn('text-base font-bold', t.text)}>{d.dayNumber}</span>
                <span className={cn('text-[10px]', t.mutedText)}>({d.count})</span>
              </div>
            ))}
          </div>

          {/* Barra completa de acciones */}
          <div className="flex items-center justify-center gap-0.5 sm:gap-2">
            <CircleButton Icon={LockKeyhole} dark={darkMode} />
            <CircleButton Icon={Globe} highlighted={isQuickAccessSaved} actionHighlight={isWebAction} interactive={isWebAction} onClick={onOpenWeb} />
            <CircleButton Icon={ImageIcon} highlighted={isQuickAccessSaved} actionHighlight={isImageAction} interactive={isImageAction} onClick={onOpenImage} />
            <CircleButton Icon={Home} dark={darkMode} />
            <CircleButton Icon={CalendarDays} dark={darkMode} />
            <CircleButton Icon={Check} dark={darkMode} />
            <CircleButton Icon={Plus} dark={darkMode} />
          </div>

          {/* CoachMark de Web/Imagen */}
          {(isQuickAccessSaved || isWebAction || isImageAction) && (
            <div className="pt-1">
              <CoachMark text={message} />
              {isQuickAccessSaved && (
                <div className="mt-3">
                  <ContinueButton onClick={onContinue} />
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 flex items-center justify-center gap-2">
          <div className={cn('flex h-10 w-10 items-center justify-center rounded-full border opacity-40', t.mutedBg)}>
            <Globe className={cn('h-5 w-5', t.mutedText)} />
          </div>
          <div className={cn('flex h-10 w-10 items-center justify-center rounded-full border opacity-40', t.mutedBg)}>
            <ImageIcon className={cn('h-5 w-5', t.mutedText)} />
          </div>
        </div>
      )}
    </div>
  );
}

function SimulatedSettings({
  activeTarget,
  message,
  confirmImportantActions,
  onContinue,
  onToggleConfirm,
  onSaveSettings,
}) {
  const isProfileInfo = activeTarget === 'profile-info';
  const isWebInfo = activeTarget === 'web-info';
  const isImageInfo = activeTarget === 'image-info';
  const isConfirmActions = activeTarget === 'confirm-actions';
  const isConfirmPreview = activeTarget === 'confirm-preview';
  const isPinInfo = activeTarget === 'pin-info';
  const isSaveSettings = activeTarget === 'save-settings';

  return (
    <div className="rounded-lg border bg-background">
      <div className="border-b p-4 text-center">
        <h3 className="text-base font-semibold">Configuración de {PROFILE_NAME}</h3>
      </div>

      <div className="space-y-3 p-4">
        {/* Tarjeta 1: Datos del perfil */}
        <div
          className={cn(
            'space-y-3 rounded-lg border bg-card p-4',
            isProfileInfo ? 'ring-2 ring-primary ring-offset-1' : ''
          )}
        >
          <h4 className="text-base font-semibold">Datos del perfil</h4>
          <div>
            <Label className="text-sm font-medium">Nombre del Perfil</Label>
            <Input value={PROFILE_NAME} readOnly disabled className="mt-1" />
          </div>
          <div>
            <Label className="text-sm font-medium">WhatsApp (teléfono)</Label>
            <Input value={PROFILE_PHONE} readOnly disabled className="mt-1" />
          </div>
        </div>

        {isProfileInfo && (
          <>
            <CoachMark text={message} themeAware />
            <ContinueButton onClick={onContinue} />
          </>
        )}

        {/* Tarjeta 2: Accesos rápidos */}
        <div
          className={cn(
            'space-y-3 rounded-lg border bg-card p-4',
            isWebInfo || isImageInfo ? 'ring-2 ring-primary ring-offset-1' : ''
          )}
        >
          <h4 className="text-base font-semibold">Accesos rápidos</h4>

          <div className={cn(isWebInfo ? '-m-1 rounded-md p-1 ring-2 ring-primary ring-offset-1' : '')}>
            <Label htmlFor="profile-weburl" className="text-sm font-medium">
              Web de acceso rápido (producto, tiempo, web oficial...)
            </Label>
            <Input
              id="profile-weburl"
              value=""
              readOnly
              disabled
              placeholder="https://..."
              className="mt-1"
            />
          </div>

          <div className={cn(isImageInfo ? '-m-1 rounded-md p-1 ring-2 ring-primary ring-offset-1' : '')}>
            <Label className="text-sm font-medium">
              Imagen de acceso rápido (planos, plazas, normas...)
            </Label>
            <div className="mt-2">
              <Button type="button" variant="outline" size="sm" disabled>
                Subir imagen
              </Button>
            </div>
          </div>
        </div>

        {isWebInfo && (
          <>
            <CoachMark text={message} themeAware />
            <ContinueButton onClick={onContinue} />
          </>
        )}

        {isImageInfo && (
          <>
            <CoachMark text={message} themeAware />
            <ContinueButton onClick={onContinue} />
          </>
        )}

        {/* Tarjeta 3: Confirmar acciones */}
        <div
          className={cn(
            'space-y-2 rounded-lg border bg-card p-4',
            isConfirmActions || isConfirmPreview ? 'ring-2 ring-primary ring-offset-1' : ''
          )}
        >
          <h4 className="text-base font-semibold">Confirmar acciones</h4>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">Confirmar acciones importantes</Label>
              <p className="text-xs text-muted-foreground">
                Pide confirmación antes de marcar clientes, anularlos o cambiar estados de ruta.
              </p>
            </div>
            <Switch
              checked={confirmImportantActions}
              onCheckedChange={onToggleConfirm}
              disabled={!isConfirmActions}
            />
          </div>

          {isConfirmPreview && (
            <div className="space-y-2 pt-2">
              <ConfirmationExample label="¿Marcar KIRA?" />
              <ConfirmationExample label="¿Anular cliente?" />
            </div>
          )}
        </div>

        {isConfirmActions && <CoachMark text={message} themeAware />}

        {isConfirmPreview && (
          <>
            <CoachMark text={message} themeAware />
            <ContinueButton onClick={onContinue} />
          </>
        )}

        {/* Tarjeta 4: Configurar PIN */}
        <div
          className={cn(
            'space-y-3 rounded-lg border bg-card p-4',
            isPinInfo ? 'ring-2 ring-primary ring-offset-1' : ''
          )}
        >
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            <h4 className="text-base font-semibold">Configurar PIN para este perfil</h4>
          </div>
          <p className="text-xs text-muted-foreground">
            Sin PIN configurado. Puedes proteger este perfil creando uno de 4 a 6 dígitos.
          </p>
          <div>
            <Label htmlFor="new-pin" className="text-xs text-muted-foreground">
              PIN nuevo (4 a 6 dígitos)
            </Label>
            <Input
              id="new-pin"
              type="password"
              value=""
              readOnly
              disabled
              maxLength={6}
              placeholder="0000"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="confirm-pin" className="text-xs text-muted-foreground">
              Repetir PIN nuevo
            </Label>
            <Input
              id="confirm-pin"
              type="password"
              value=""
              readOnly
              disabled
              maxLength={6}
              placeholder="0000"
              className="mt-1"
            />
          </div>
          <Button type="button" variant="outline" disabled className="w-full">
            Guardar PIN
          </Button>
        </div>

        {isPinInfo && (
          <>
            <CoachMark text={message} themeAware />
            <ContinueButton onClick={onContinue} />
          </>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="outline" disabled>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={onSaveSettings}
            disabled={!isSaveSettings}
            className={cn('bg-primary hover:bg-primary/90', isSaveSettings ? 'ring-2 ring-primary ring-offset-1' : '')}
          >
            Guardar Cambios
          </Button>
        </div>

        {isSaveSettings && <CoachMark text={message} themeAware />}
      </div>
    </div>
  );
}


function simTheme(dark) {
  return {
    screen: dark ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-slate-200',
    text: dark ? 'text-slate-100' : 'text-slate-900',
    mutedText: dark ? 'text-slate-400' : 'text-slate-500',
    card: dark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200',
    mutedBg: dark ? 'bg-slate-800' : 'bg-slate-100',
  };
}

function WebResultView({ darkMode, message, onContinue }) {
  const t = simTheme(darkMode);
  return (
    <div className={cn('rounded-lg border p-4', t.screen)}>
      <div className="flex justify-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full border border-primary bg-primary/15">
          <Globe className="h-6 w-6 text-primary" />
        </div>
      </div>
      <h3 className={cn('mt-3 text-center text-base font-semibold', t.text)}>Web de acceso rápido</h3>
      <div className={cn('mt-3 rounded-md border px-3 py-2 text-center', t.card)}>
        <span className={cn('text-xs', t.mutedText)}>https://ejemplo.rutapp.local</span>
      </div>
      <div className={cn('mt-3 space-y-2 rounded-md border p-3', t.card)}>
        <div className={cn('h-2 w-3/4 rounded', t.mutedBg)} />
        <div className={cn('h-2 w-1/2 rounded', t.mutedBg)} />
        <div className={cn('h-2 w-2/3 rounded', t.mutedBg)} />
      </div>
      <div className="mt-4">
        <CoachMark text={message} />
      </div>
      <div className="mt-3">
        <ContinueButton onClick={onContinue} />
      </div>
    </div>
  );
}

function ImageResultView({ darkMode, message, onContinue }) {
  const t = simTheme(darkMode);
  return (
    <div className={cn('rounded-lg border p-4', t.screen)}>
      <div className={cn('mx-auto w-full max-w-xs rounded-md border p-3', t.card)}>
        <span className={cn('text-xs font-semibold', t.mutedText)}>Plano de referencia</span>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div className="h-12 rounded border border-primary/40 bg-primary/10" />
          <div className={cn('h-12 rounded border', t.mutedBg)} />
          <div className={cn('h-12 rounded border', t.mutedBg)} />
          <div className="h-12 rounded border border-primary/40 bg-primary/10" />
        </div>
        <div className={cn('mt-2 h-1.5 w-full rounded', t.mutedBg)} />
        <div className="mt-2 flex justify-between">
          <span className={cn('text-[10px]', t.mutedText)}>Zona A</span>
          <span className={cn('text-[10px]', t.mutedText)}>Zona B</span>
        </div>
      </div>
      <div className="mt-4">
        <CoachMark text={message} />
      </div>
      <div className="mt-3">
        <ContinueButton onClick={onContinue} />
      </div>
    </div>
  );
}

function SimulatedComplete({ onRestartTutorial, onExitTutorial, exitLabel = 'Volver a Tutoriales' }) {
  return (
    <div className="rounded-lg border bg-background p-6 text-center">
      <h3 className="text-lg font-semibold">Has completado Perfil, accesos y apariencia.</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Ya sabes utilizar tus accesos rápidos y personalizar tu experiencia en Rutapp.
      </p>
      <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
        <Button type="button" variant="outline" onClick={onRestartTutorial}>
          Repetir tutorial
        </Button>
        <Button type="button" onClick={onExitTutorial}>
          {exitLabel}
        </Button>
      </div>
    </div>
  );
}

export function ProfileAccessAppearanceTutorial({ onExit, completionActionLabel, onCompletionAction }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmImportantActions, setConfirmImportantActions] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const resetScenario = () => {
    setSettingsOpen(false);
    setConfirmImportantActions(false);
    setDarkMode(false);
  };

  const handleBack = (targetStep) => {
    if (!targetStep) return;

    switch (targetStep.id) {
      case 'settings-action':
        setSettingsOpen(false);
        break;
      case 'confirm-actions':
        setConfirmImportantActions(false);
        break;
      case 'save-settings':
        setSettingsOpen(true);
        break;
      case 'theme-action':
        setDarkMode(false);
        break;
      case 'theme-back':
        setDarkMode(true);
        break;
      default:
        break;
    }
  };

  const renderStep = (step, index, actions) => {
    const goNext = actions?.goNext ?? (() => {});
    const restart = actions?.restart ?? (() => {});
    const exit = actions?.exit ?? (() => {});
    const completeAction = onCompletionAction ?? exit;
    const completeLabel = completionActionLabel ?? 'Volver a Tutoriales';

    const handleOpenSettings = () => {
      if (step.id !== 'settings-action') return;
      setSettingsOpen(true);
      goNext();
    };

    const handleContinue = () => {
      goNext();
    };

    const handleToggleConfirm = (checked) => {
      if (step.id !== 'confirm-actions') return;
      setConfirmImportantActions(checked);
      goNext();
    };

    const handleSaveSettings = () => {
      if (step.id !== 'save-settings') return;
      setSettingsOpen(false);
      goNext();
    };

    const handleOpenWeb = () => {
      if (step.id !== 'web-action') return;
      goNext();
    };

    const handleOpenImage = () => {
      if (step.id !== 'image-action') return;
      goNext();
    };

    const handleToggleTheme = () => {
      if (step.id === 'theme-action') {
        setDarkMode(true);
        goNext();
      } else if (step.id === 'theme-back') {
        setDarkMode(false);
        goNext();
      }
    };

    if (step.id === 'tutorial-complete') {
      return <SimulatedComplete onRestartTutorial={restart} onExitTutorial={completeAction} exitLabel={completeLabel} />;
    }

    if (step.id === 'web-result') {
      return <WebResultView darkMode={darkMode} message={step.message} onContinue={handleContinue} />;
    }

    if (step.id === 'image-result') {
      return <ImageResultView darkMode={darkMode} message={step.message} onContinue={handleContinue} />;
    }

    if (settingsOpen) {
      return (
        <SimulatedSettings
          activeTarget={step.target}
          message={step.message}
          confirmImportantActions={confirmImportantActions}
          onContinue={handleContinue}
          onToggleConfirm={handleToggleConfirm}
          onSaveSettings={handleSaveSettings}
        />
      );
    }

    return (
      <SimulatedMainScreen
        activeTarget={step.target}
        message={step.message}
        darkMode={darkMode}
        onOpenSettings={handleOpenSettings}
        onOpenWeb={handleOpenWeb}
        onOpenImage={handleOpenImage}
        onToggleTheme={handleToggleTheme}
        onContinue={handleContinue}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>Perfil, accesos y apariencia</DialogTitle>
        <DialogDescription>
          Configura tu perfil y tus accesos rápidos en un entorno de práctica.
        </DialogDescription>
      </DialogHeader>

      <TutorialEngine
        steps={STEPS}
        renderStep={renderStep}
        onExit={onExit}
        onRestart={resetScenario}
        onBack={handleBack}
        showStepNavigation={false}
      />
    </div>
  );
}
